import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { BadgeCheck, Loader2, RefreshCw, Link2, Unlink } from "lucide-react";
import DuprConnectModal from "@/components/DuprConnectModal";

const fmt = (v) => (v === null || v === undefined ? "NR" : Number(v).toFixed(3));

export default function DuprLinkCard() {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [open, setOpen] = useState(false);
  const load = () => api.get("/dupr/me").then(({ data }) => setData(data)).catch(() => setData({ link: null, sso_enabled: false }));
  useEffect(() => { load(); }, []);
  const refresh = async () => {
    setBusy(true);
    try { const { data } = await api.post("/dupr/me/refresh"); setData((d) => ({ ...d, link: data.link })); toast.success("DUPR rating refreshed"); }
    catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  const unlink = async () => {
    if (!window.confirm("Disconnect your DUPR account from Pickle Bullpen?")) return;
    setBusy(true);
    try { await api.delete("/dupr/me/link"); toast.success("DUPR disconnected"); load(); }
    catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  if (!data) return null;
  const link = data.link;
  if (!link) {
    if (!data.sso_enabled) return null;
    return (
      <div className="mt-3 border border-pb-border/70 bg-black/30 p-3 text-xs flex flex-wrap items-center gap-3" data-testid="dupr-connect-card">
        <span className="text-zinc-300">Connect your <b className="text-white">DUPR</b> account to show your verified rating{data.env !== "production" ? " (sandbox)" : ""}.</span>
        <button data-testid="dupr-connect-btn" onClick={() => setOpen(true)} className="ml-auto inline-flex items-center gap-1.5 border border-pb-cyan text-pb-cyan px-3 py-1.5 text-[10px] font-black uppercase tracking-widest hover:bg-pb-cyan hover:text-black">
          <Link2 className="w-3 h-3" /> Connect DUPR
        </button>
        <DuprConnectModal open={open} onClose={() => setOpen(false)} onLinked={() => load()} />
      </div>
    );
  }
  const ent = (link.entitlements || {}).tournaments || [];
  return (
    <div className="mt-3 border border-pb-cyan/40 bg-pb-cyan/5 p-3 text-xs" data-testid="dupr-link-card">
      <div className="flex items-center gap-2 flex-wrap">
        <BadgeCheck className="w-4 h-4 text-pb-cyan" />
        <span className="font-black uppercase tracking-widest text-pb-cyan">Verified DUPR</span>
        <span className="font-mono text-zinc-300" data-testid="dupr-link-id">{link.dupr_id}</span>
        {data.env !== "production" && <span className="text-[10px] uppercase tracking-widest text-amber-400 border border-amber-400/40 px-1.5">{data.env} sandbox</span>}
        <span className="ml-auto flex gap-3">
          <button data-testid="dupr-link-refresh" onClick={refresh} disabled={busy} className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest text-zinc-300 hover:text-white disabled:opacity-40">
            {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCw className="w-3 h-3" />} Refresh rating
          </button>
          <button data-testid="dupr-link-unlink" onClick={unlink} disabled={busy} className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest text-zinc-500 hover:text-red-400 disabled:opacity-40"><Unlink className="w-3 h-3" /> Disconnect</button>
        </span>
      </div>
      <div className="mt-2 flex gap-4 text-zinc-300" data-testid="dupr-link-ratings">
        <span>Doubles <b className="text-white">{fmt(link.ratings?.doubles)}</b>{link.ratings?.doubles_reliable ? "" : <span className="text-zinc-500"> (unreliable)</span>}</span>
        <span>Singles <b className="text-white">{fmt(link.ratings?.singles)}</b>{link.ratings?.singles_reliable ? "" : <span className="text-zinc-500"> (unreliable)</span>}</span>
      </div>
      <div className="mt-1 text-[10px] text-zinc-500" data-testid="dupr-link-synced">
        {link.synced_at ? `Last synced ${new Date(link.synced_at).toLocaleString()}` : "Not synced yet"}{link.stale ? " · stale" : ""}{link.rating_source ? ` · via ${link.rating_source.toLowerCase()}` : ""}
        {link.last_error && <span className="text-red-400"> · last attempt failed: {link.last_error}</span>}
      </div>
      {ent.length > 0 && <div className="mt-1 text-[10px] text-zinc-500" data-testid="dupr-link-entitlements">DUPR eligibility: {ent.join(", ")}</div>}
      <p className="mt-1 text-[10px] text-zinc-600">Shown for reference only — your PSI, PBR points and eligibility are unchanged by DUPR data.</p>
    </div>
  );
}
